import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FundListDetail } from '@nemo/api';
import { useId, useState } from 'react';
import { useLocation } from 'wouter';
import { useFundNameSearch } from '../grid/useFundNameSearch.js';
import { useTRPC } from '../trpc.js';
import { Popover } from '../ui/Popover.js';
import { useToast } from '../ui/Toast.js';
import { setAddTarget } from './addTarget.js';

const MAX_RESULTS = 50;

/**
 * Funds onto the open list without leaving it: a search over fund names and
 * aliases that stays open for the next one, and the way across to the funds
 * table when the filters are what finds them.
 */
export function AddFundsSearch(props: { list: FundListDetail }): React.JSX.Element {
  const { list } = props;
  const listboxId = useId();
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const [input, setInput] = useState<HTMLInputElement | null>(null);
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const add = useMutation(
    trpc.lists.addItems.mutationOptions({
      onSuccess: (answer) => {
        if (answer.list !== null)
          queryClient.setQueryData(trpc.lists.get.queryKey({ id: list.id }), answer.list);
        void queryClient.invalidateQueries({ queryKey: trpc.lists.list.queryKey() });
        void queryClient.invalidateQueries(trpc.funds.search.pathFilter());
      },
      onError: (error) => toast(error.message),
    }),
  );

  const search = useFundNameSearch(text, { limit: MAX_RESULTS, notOnList: list.id });
  const results = search.matches;

  const optionId = (index: number): string => `${listboxId}-option-${index}`;
  const shown = open && text.trim() !== '';
  const current = Math.min(active, Math.max(0, results.length - 1));

  function choose(index: number): void {
    // Enter must not add a fund the last search found but this text no longer means.
    const fund = search.settled ? results[index] : undefined;
    if (fund === undefined || add.isPending) return;
    add.mutate({ id: list.id, fundIds: [fund.id] });
    setText('');
    setActive(0);
    input?.focus();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setOpen(true);
        setActive(results.length === 0 ? 0 : (current + 1) % results.length);
        return;
      case 'ArrowUp':
        event.preventDefault();
        setActive(results.length === 0 ? 0 : (current - 1 + results.length) % results.length);
        return;
      case 'Enter':
        event.preventDefault();
        choose(current);
        return;
      default:
        return;
    }
  }

  return (
    <div className="add-funds">
      <input
        ref={setInput}
        className="search"
        role="combobox"
        aria-label="Add funds"
        aria-expanded={shown}
        aria-controls={shown ? listboxId : undefined}
        aria-activedescendant={shown && results.length > 0 ? optionId(current) : undefined}
        aria-autocomplete="list"
        placeholder="Add funds…"
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {shown && input !== null && (
        <Popover anchor={{ kind: 'element', element: input }} onClose={() => setOpen(false)}>
          <ul id={listboxId} role="listbox" aria-label="Funds to add" className="listbox">
            {results.map((fund, index) => (
              <li
                key={fund.id}
                id={optionId(index)}
                role="option"
                aria-selected={index === current}
                className={index === current ? 'listbox-option active' : 'listbox-option'}
                onMouseMove={() => setActive(index)}
                // The pointer must not take focus off the input, or a click
                // would close the list before it lands.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(index)}
              >
                {fund.name}
              </li>
            ))}
            {results.length === 0 && search.settled && (
              <li className="listbox-empty subtle">No fund outside this list matches.</li>
            )}
          </ul>
        </Popover>
      )}
      <button
        className="link-button"
        type="button"
        onClick={() => {
          setAddTarget(list.id);
          navigate('/');
        }}
      >
        Pick from the funds table
      </button>
    </div>
  );
}
